import { h } from '@stencil/core';
import { CaptchaDetails, TokenAnswer } from '../../forms-ui-nucaptcha/types';
import { CONSTANTS } from '../constant';
import { FormSubmissionCaptchaDetails } from '../forms-ui-form-payload.interface';
import { FormsUiNucaptcha } from '../../forms-ui-nucaptcha/forms-ui-nucaptcha';

export interface INucaptchaService {
  renderCaptchaContainer(): FormsUiNucaptcha;
  prepareCaptchaDetails(): Promise<FormSubmissionCaptchaDetails>;
  handleCaptchaResponse(captchaDetails: CaptchaDetails): Promise<void>;
  cleanUpCaptcha(): void;
}

export interface NucaptchaConfiguration {
	label: string;
	validationMessage: string;
	reportSegment: string;
	clientId: string;
}

export class NucaptchaService implements INucaptchaService {
	config: NucaptchaConfiguration;

	captchaRef: HTMLFormsUiNucaptchaElement = null;

	actionType: string = CONSTANTS.ACTION_SCORE_KEY;

	constructor(config: NucaptchaConfiguration) {
		this.config = config;
	}

	/** nucaptcha error codes */
	nucaptchaReasonCodes = {
		nuCaptchaScoreFailure: CONSTANTS.CAPTCHA_REQUIRED,
		nuCaptchaValidateFailure: CONSTANTS.INVALID_CAPTCHA,
		nuCaptchaBlankValueFailure: CONSTANTS.BLANK_CATPCHA,
	};

	renderCaptchaContainer(): FormsUiNucaptcha {
		return (
			<forms-ui-nucaptcha
				ref={(ref) => this.captchaRef = ref}
				client-id={this.config.clientId}
				base-url={CONSTANTS.CAPTCHA_BASE_URL}
				label={this.config.label}
				placement={CONSTANTS.CAPTCHA_PLACEMENT}
				placement-page="1">
			</forms-ui-nucaptcha>
		);
	}

	async prepareCaptchaDetails(): Promise<FormSubmissionCaptchaDetails> {
		const widgetData = await this.captchaRef.getWidgetData();
		const { actionType } = this;
		const { reportSegment } = this.config;
		/** There is a typo here, but this is what the API expects... */
		let captchValidate: TokenAnswer;
		if (actionType === CONSTANTS.ACTION_VALIDATE_KEY) {
			captchValidate = await this.captchaRef.getTokenAnswer();
		}

		return {
			widgetData,
			captchValidate,
			actionType,
			transactionParams: {
				reportSegment,
			},
		};
	}

	/** form submit captcha exception handler */
	async handleCaptchaResponse(captchaDetails: CaptchaDetails): Promise<void> {
		let event;
		const { validationMessage } = this.config;

		const toast = (message: string, variant: string) => {
			event = new CustomEvent(
				'toast',
				{
					detail: {
						message,
						variant,
						light: false,
						timeout: 2500,
					},
				},
			);
			document.dispatchEvent(event);
		};

		switch (captchaDetails.reasonCode) {
			case this.nucaptchaReasonCodes.nuCaptchaScoreFailure:
				toast(captchaDetails.description, 'information');
				await this.captchaRef.renderCaptcha(captchaDetails.captchaHtml);
				break;
			case this.nucaptchaReasonCodes.nuCaptchaValidateFailure:
				toast(validationMessage ?? 'Invalid Captcha response. Please try again.', 'error');
				await this.captchaRef.renderCaptcha(captchaDetails.captchaHtml);
				break;
			case this.nucaptchaReasonCodes.nuCaptchaBlankValueFailure:
				toast(validationMessage ?? 'Captcha response is required.', 'error');
				break;
			default:
				throw new Error(`Invalid Captcha reason code received. Code: ${captchaDetails.reasonCode}`);
		}

		this.actionType = CONSTANTS.ACTION_VALIDATE_KEY;
	}

	cleanUpCaptcha(): void {
		this.actionType = CONSTANTS.ACTION_SCORE_KEY;
		this.captchaRef.removeCaptcha();
		this.captchaRef.resetCaptcha();
	}
}

export const createNucaptchaService = (config: NucaptchaConfiguration)
	: INucaptchaService => new NucaptchaService(config);
